import React, { useCallback, useRef, useEffect, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import FamtourCard, { FamtourCardProps } from "@/Components/FamtourCard";
import { ChevronRightCircle, ChevronUp } from "lucide-react";
import formatImages from "@/utils/formatImages.ts";
import clsx from "clsx";
import { useTheme } from "styled-components";
import { useThemeAsset } from "@/Hooks/useThemeAsset.ts";
import { ApiResponse, FamtourItem } from "@/types";
import { useThemeClass } from "@/Hooks/useThemeClass.ts";
import { Link } from "react-router-dom";
import weuTripSvg from "@/assets/weu-trip.svg";
export const BASE_URL = import.meta.env.VITE_API_URL;

export const FAMTOUR_ID = 18; // TODO: This should not be hardcoded
export const FAMTOUR_QUERY_KEY = "famtour-list";

const fetchFamtourList = async ({
  pageParam = 1,
  sort = "reg_start_date",
  per_page = "10",
}): Promise<FamtourItem[]> => {
  const res = await fetch(
    `${BASE_URL}/user/slot/famtour?offset=${pageParam}&per_page=${per_page}&sorting=${sort}`
  );
  const json: ApiResponse = await res.json();
  return json.data.data;
};

type FamtourListProps = {
  maxItem?: number | string;
};

const FamtourList: React.FC = ({ maxItem }: FamtourListProps) => {
  const theme = useTheme();
  const themeClass = useThemeClass();
  const themeAsset = useThemeAsset();
  const [sortOption, setSortOption] = useState("reg_start_date");
  const queryClient = useQueryClient();
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useInfiniteQuery<FamtourItem[]>({
      queryKey: [FAMTOUR_QUERY_KEY, sortOption],
      queryFn: ({ pageParam = 1 }) =>
        fetchFamtourList({ pageParam, sort: sortOption, per_page: maxItem }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === 10 ? allPages.length + 1 : undefined,
      initialPageParam: 1,
    });

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    type SortKey = "latest" | "popular" | "deadline";
    const sortMap: Record<SortKey, string> = {
      latest: "reg_start_date",
      popular: "total_apply",
      deadline: "announcement",
    };

    setSortOption(sortMap[e.target.value as SortKey]);
    queryClient.removeQueries({ queryKey: [FAMTOUR_QUERY_KEY] });
  };

  const observer = useRef<IntersectionObserver | null>(null);
  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setShowButton(window.scrollY > 300);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const famtourList: any = data?.pages.flat() ?? [];

  const isHomepage = location.pathname === "/";
  if (isHomepage && famtourList.length < 1) {
    return null;
  }

  return (
    <div className="relative">
      <div
        className={clsx(
          `text-xl mb-1 text-[#1D2C59] flex justify-between items-center sticky top-[56px] bg-white z-10  py-1`
        )}
      >
        <div
          className={
            location.pathname === "/mypage" ||
            location.pathname === "/interest-campaign"
              ? "hidden"
              : "absolute inset-0 w-screen left-1/2 -translate-x-1/2 -z-10"
          }
          style={{ background: theme.colors.bgRecent }}
        />
        {location.pathname === "/" ? (
          <div className="flex justify-between w-full">
            <div className={clsx(!themeAsset.grayscaleLogo && "")}>
              <img
                src={weuTripSvg}
                alt="위유트립"
                className="h-[30px] w-[90px]"
              />
            </div>
            <Link to={"/detailcategory/위유트립"}>
              <ChevronRightCircle width={26} height={26} />
            </Link>
          </div>
        ) : location.pathname === "/mypage" ||
          location.pathname === "/interest-campaign" ? null : (
          <div className=" py-[8px]">
            <select
              className="ps-2 text-sm rounded py-1 border-gray-300 text-gray-700"
              onChange={handleSortChange}
            >
              <option value="latest">최신순</option>
              <option value="popular">인기순</option>
              <option value="deadline">마감임박순</option>
            </select>
          </div>
        )}
      </div>
      <div className="grid lg:grid-cols-4 gap-3 xl:grid-cols-6 xl:gap-9 xl:mt-5 grid-cols-2 pb-2">
        {famtourList.map((famtour: any, index: number) => {
          const images = formatImages(famtour?.images);
          famtour = { ...famtour, images };

          return (
            <FamtourCard
              key={famtour.id}
              ref={index === famtourList.length - 1 ? lastItemRef : null}
              item={famtour as FamtourCardProps["item"]}
            />
          );
        })}
      </div>

      {isFetching && (
        <div className="flex justify-center items-center py-6">
          <div className="w-10 h-10 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin" />
        </div>
      )}

      {showButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-[5%] right-5 bg-white text-black p-3 rounded-full shadow-lg hover:bg-[#46B6D0] transition top-button"
        >
          <ChevronUp size={24} />
        </button>
      )}
    </div>
  );
};

export default FamtourList;
